import {Share} from '@mui/icons-material';
import {Box, Breadcrumbs, Card, CardContent, Chip, IconButton, Link, Stack, TextField, Tooltip, Typography} from '@mui/material';
import {useDebounce} from '@zs21/drv-bund-common-ilias-utils';
import React, {Fragment, useState} from 'react';
import CustomModal from '../CustomModal';
import {ENV_VARS} from '../envVars';

export const CourseSearchModal = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filteredCourses, setFilteredCourses] = useState([]);
    const debouncedSearch = useDebounce((query) => {
        const queryLowerCase = query.toLowerCase();
        setFilteredCourses(courses.filter((item) => {
            return item.title.toLowerCase().includes(queryLowerCase) || item.description.toLowerCase().includes(queryLowerCase);
        }));
    });
    const MINIMUM_QUERY_LENGTH = 3;
    const text = (() => {
        if (loading) {
            return 'Inhalte werden geladen';
        }

        if (query.length < MINIMUM_QUERY_LENGTH) {
            return `Mindestens ${MINIMUM_QUERY_LENGTH} Zeichen zum Suchen eingeben`;
        }

        if (filteredCourses.length === 0) {
            return 'Keine Kurse gefunden';
        }

        return null;
    })();

    const handleClose = () => {
        setFilteredCourses([]);
        setQuery('');
        setModalOpen(false);
    };

    const handleChange = (event) => {
        const currentQuery = event.target.value;
        setQuery(currentQuery);
        if (currentQuery.length >= 3) {
            debouncedSearch(currentQuery);
        }
    };

    window.openCourseSearch = () => {
        setModalOpen(true);
        setLoading(true);

        fetch(ENV_VARS['LIST_CONTENTS_URI'], {credentials: 'include'})
            .then(res => res.json())
            .then(data => {
                setCourses(data.data.filter((item) => item.iliasObjType === 'crs'));
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    return <CustomModal
        maxWidth={'lg'}
        open={modalOpen}
        onClose={handleClose}
        title={'Kurssuche'}
        isProcessing={loading}
    >
        <TextField
            label="Sucheingabe"
            value={query}
            type={'search'}
            variant="outlined"
            fullWidth
            onChange={handleChange}
            inputProps={{sx: {boxSizing: 'content-box !important'}}}
            sx={{mt: 1}}
        />
        {null === text && <Box sx={{maxHeight: 400, overflowY: 'auto', mt: 2, p: 1, pr: 2}}>
            <Stack spacing={2}>
                {filteredCourses.map((item) => <Fragment key={`course-${item.refId}`}>
                    <CourseCard item={item} />
                </Fragment>)}
            </Stack>
        </Box>}
        {null !== text && <>
            <Box mt={2}>
                <Typography variant={'body1'}>{text}</Typography>
            </Box>
        </>}
    </CustomModal>;
};

const CourseCard = ({item: {url, title, description, breadcrumb, isPublic, shownInCatalogue, online, joinLink}}) => {
    return <>
        <Card sx={{display: 'flex'}}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%'}}>
                <CardContent>
                    <Typography component={'div'} variant={'body1'}>
                        <Link target={'_blank'} href={url}>{title}</Link>
                    </Typography>
                    <Breadcrumbs separator="›" maxItems={2} sx={{fontSize: '.7rem', mt: 1}}>
                        {breadcrumb.slice(0, -1).map((crumb, index) =>
                            <Link key={`breadcrumb-${index}`} target={'_blank'} href={crumb.url} color={`text.secondary`}>{crumb.title}</Link>
                        )}
                    </Breadcrumbs>
                    {description && <Typography variant={'body2'} sx={{mt: 2}}>{description}</Typography>}
                </CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', space: 1, px: 2, pb: 1, color: 'text.secondary' }}>
                    <Stack direction={'row'} spacing={1}>
                        <Chip
                            color={online ? 'success' : 'error'}
                            label={online ? 'online' : 'offline'}
                            variant={'outlined'}
                            size={'small'}
                        />
                        {shownInCatalogue && <Chip label={'Im Selbstlernkatalog'} variant={'outlined'} size={'small'} />}
                        {!isPublic && <Chip label={'Zugriff eingeschränkt'} variant={'outlined'} size={'small'} />}
                    </Stack>
                    {joinLink && <Tooltip title={'Teilnahmelink kopieren'}>
                        <IconButton aria-label={'Teilnahmelink kopieren'} sx={{ml: 'auto'}} onClick={() => {
                            navigator.clipboard.writeText(joinLink);
                        }}>
                            <Share/>
                        </IconButton>
                    </Tooltip>}
                </Box>
            </Box>
        </Card>
    </>;
};
